import { BenefitType } from '@/types/benefits/benefitTypes';
import { CircuitResponse } from '@/types/circuits/CircuitResponse';
import { SelectedEstimateOption } from '@/types/estimates/SelectedEstimateOptionType';
import { toHotelPensionType } from '@/constants/hotelPension';

type StepHotelDefaults = {
  standardBenefitId: string;
  defaultCategoryId?: string;
  defaultPension?: string;
};

/**
 * Build stepId → standard hotel defaults from the estimate circuit.
 * Used to detect auto-persisted "ghost" standard hotel options.
 */
export const buildStandardHotelDefaultsByStepId = (
  circuit: CircuitResponse,
): Map<string, StepHotelDefaults> => {
  const map = new Map<string, StepHotelDefaults>();
  for (const day of circuit.circuitDays ?? []) {
    for (const step of day.CircuitDayStep ?? []) {
      const standard = step.standardBenefit;
      if (!standard?.id) continue;
      if (standard.type !== BenefitType.HOTEL && standard.type !== 'Hébergement')
        continue;

      const accommodationStandard = step.accommodationSelections?.find(
        (selection) =>
          selection.selectionType === 'STANDARD' &&
          selection.benefitId === standard.id,
      );

      const defaultCategoryId = standard.hotel?.HotelRoomCategory?.[0]?.id;
      const defaultPension =
        toHotelPensionType(
          accommodationStandard?.pensionType ??
            standard.hotel?.pricingPensionType ??
            undefined,
        ) ?? undefined;

      map.set(step.id, {
        standardBenefitId: standard.id,
        defaultCategoryId: defaultCategoryId ?? undefined,
        defaultPension,
      });
    }
  }
  return map;
};

/**
 * True when the selected hotel option is still the included standard hotel
 * (same benefit, default category & pension). Spurious deltas from room-combo
 * vs minimumPrice mismatches must not appear as "Option Hébergement +X €".
 */
export const isDefaultStandardHotelOption = (
  option: Pick<
    SelectedEstimateOption,
    'benefitId' | 'hotelCategoryId' | 'hotelPensionType' | 'type'
  >,
  defaults: StepHotelDefaults | undefined,
): boolean => {
  if (!defaults) return false;
  if (option.type !== BenefitType.HOTEL) return false;
  if (option.benefitId !== defaults.standardBenefitId) return false;

  const categoryMatches =
    !defaults.defaultCategoryId ||
    !option.hotelCategoryId ||
    option.hotelCategoryId === defaults.defaultCategoryId;

  const optionPension = toHotelPensionType(option.hotelPensionType);
  const pensionMatches =
    !defaults.defaultPension ||
    !optionPension ||
    optionPension === defaults.defaultPension;

  return categoryMatches && pensionMatches;
};
